import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../rootReducer';
import { Ifilter } from '../../components/common/filters/interfaces';
import { ActiveInactiveFilterState } from '../../components/common/activeInactiveToggle';
import { TableSort } from '../../utils/dataTable/data';
import { SortStatus } from '../../components/common/sortButton';
import { AccessTypes } from '../../components/common/domain/accountAccessType';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

export interface DashboardFilterState {
  searchQuery: string;
  searchFilters : Ifilter[];
  entryStateFilter: ActiveInactiveFilterState;
  sortParameters: TableSort;
  // use for Features.PlgExternal3
  selectedAccountType: AccessTypes | null;
  // end of use for Features.PlgExternal3
}

const initialState: DashboardFilterState = {
  searchQuery: '',
  searchFilters: [],
  entryStateFilter: ActiveInactiveFilterState.Active,
  sortParameters: { status: SortStatus.None },
  selectedAccountType: AccessTypes.Enterprise
};

const getDashboardFilterState = (state: RootState): DashboardFilterState => state.filters.dashboard;
const getSearchQuery = (state: RootState): string => getDashboardFilterState(state).searchQuery;
const getSearchFilters = (state: RootState): Ifilter[] => getDashboardFilterState(state).searchFilters;
const getEntryStatus = (state: RootState): ActiveInactiveFilterState => getDashboardFilterState(state).entryStateFilter;
const getSorter = (state: RootState): TableSort => getDashboardFilterState(state).sortParameters;
const getAccountType = (state: RootState): AccessTypes | null => getDashboardFilterState(state).selectedAccountType;

export const selectors = {
  getSearchQuery,
  getSearchFilters,
  getEntryStatus,
  getSorter,
  getAccountType
};

const reducers = {
  setSearchFilters: (state: DashboardFilterState, action: PayloadAction<Ifilter[]>) => {
    state.searchFilters = action.payload;
  },
  setSearchQuery: (state: DashboardFilterState, action: PayloadAction<string>) => {
    state.searchQuery = action.payload;
  },
  setEntryState: (state: DashboardFilterState, action: PayloadAction<ActiveInactiveFilterState>) => {
    state.entryStateFilter = action.payload;
  },
  resetFilters:  (state: DashboardFilterState) => {
    state.searchFilters = [];
    state.searchQuery = '';
    state.entryStateFilter = ActiveInactiveFilterState.Active;
    state.sortParameters.status = SortStatus.None;
    state.selectedAccountType = AccessTypes.Enterprise;
  },
  setSorter: (state: DashboardFilterState, action: PayloadAction<TableSort>) => {
    state.sortParameters.key = action.payload.key;
    state.sortParameters.status = action.payload.status;
  },
  setAccountType: (state: DashboardFilterState, action: PayloadAction<AccessTypes | null>) => {
    if(!isFeatureEnabled(Features.PlgExternal3)) {
      return;
    }
    state.selectedAccountType = action.payload;
  }
};

const dashboardFilterSlice = createSlice({
  name: 'dashboardFilters',
  reducers,
  initialState
});

export const actions = { ...dashboardFilterSlice.actions };
export default dashboardFilterSlice.reducer;
